<script setup lang="ts">
import type { NewSuggestInputProps } from '.';

import { CommandInput } from '@/components/SearchSuggestRadixCommand';
import { cn } from '@/utils';

defineOptions({
	inheritAttrs: false
});

const emit = defineEmits<{
	(e: 'focus', payload: FocusEvent): void;
	(e: 'blur', payload: FocusEvent): void;
	(e: 'clear'): void;
	(e: 'close-modal'): void;
	(
		e: 'enter',
		event: KeyboardEvent,
		selectedElement: HTMLElement | undefined
	): void;
}>();

const props = withDefaults(
	defineProps<
		NewSuggestInputProps & {
			placeholder?: string;
			openInputPlaceholder?: string;
			floatingLabel?: boolean;
		}
	>(),
	{
		placeholder: '',
		showCloseIcon: false,
		floatingLabel: true
	}
);

const modelValue = defineModel<string>('modelValue');

const isFocused = shallowRef<boolean>(false);

const displayPlaceholder = computed<string>(() => props.placeholder || '');

function handleFocus(event: FocusEvent) {
	isFocused.value = true;
	emit('focus', event);
}

function handleBlur(event: FocusEvent) {
	isFocused.value = false;
	emit('blur', event);
}

function handleClear() {
	emit('clear');
}

const commandInputEl =
	useTemplateRef<InstanceType<typeof CommandInput>>('commandInputEl');

function focus() {
	const inputElement = commandInputEl.value?.$el?.querySelector('input');
	if (inputElement) {
		inputElement.focus();
	}
}

function blur() {
	const inputElement = commandInputEl.value?.$el?.querySelector('input');
	if (inputElement) {
		inputElement.blur();
	}
}

defineExpose({
	focus,
	blur
});

function handleEnter(
	event: KeyboardEvent,
	selectedElement: HTMLElement | undefined
) {
	event.preventDefault();
	event.stopPropagation();

	emit('enter', event, selectedElement);
}

const isFloating = computedAsync<boolean>(async () => {
	// Always float when focused
	if (isFocused.value) return true;

	const hasSomethingWritten = (): boolean => !!modelValue.value?.length;

	/**
	 ** This is a workaround for a race condition where selecting an option
	 ** causes the modelValue to flicker (empty -> new value), making the label
	 ** jump up and down really fast.
	 ** We wait for the modelValue to stabilize to a non-empty string.
	 ** NOTE: The root cause of the flicker should be investigated for a more
	 ** robust solution.

	 ** The timeout is set to 100ms to avoid blocking the UI for too long, and 
	 ** using a shorter timeout does not work as expected.
	 */
	await until(hasSomethingWritten).toBe(true, { timeout: 100 });

	return hasSomethingWritten();
}, false);

const focusedInputPlaceholder = computed<string>(() =>
	isFocused.value ? props.openInputPlaceholder || '' : ''
);

// Resolve actual input placeholder considering floating label mode
const inputPlaceholder = computed<string>(() => {
	if (props.floatingLabel === false) {
		return props.placeholder || '';
	}
	return focusedInputPlaceholder.value;
});
</script>

<template>
	<div class="relative">
		<CommandInput
			ref="commandInputEl"
			v-model="modelValue"
			:placeholder="inputPlaceholder"
			:class="
				cn('pt-4', props.class, {
					'placeholder:opacity-0 placeholder:transition-opacity placeholder:duration-300 placeholder:ease-in-out focus:placeholder:opacity-100':
						floatingLabel
				})
			"
			:wrapper-classes="wrapperClasses"
			:disabled="disabled"
			@focus="handleFocus"
			@blur="handleBlur"
			@enter.stop.prevent="handleEnter"
		>
			<template #prefix>
				<slot name="prefix" />
			</template>
			<template #suffix>
				<slot name="suffix">
					<div
						:class="
							cn(
								'flex h-full w-7 items-center justify-end',
								props.inputClearIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightCancel
							v-show="modelValue"
							:class="
								cn(
									'size-5 cursor-pointer text-white-dark-3',
									props.inputClearIconClasses
								)
							"
							@mousedown.prevent.stop="handleClear"
						/>
					</div>
					<button
						v-if="props.showCloseIcon"
						class="mr-4 flex size-8 items-center justify-center"
						@mousedown.prevent
						@click.stop="emit('close-modal')"
					>
						<IconMaterialSymbolsLightClose class="size-8 text-primary" />
					</button>
					<div
						:class="
							cn(
								'grid h-full w-[62px] place-items-center',
								props.inputSearchIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightSearch
							:class="cn('size-7 text-primary', props.inputSearchIconClasses)"
						/>
					</div>
				</slot>
			</template>
		</CommandInput>

		<label
			v-if="props.floatingLabel !== false"
			:class="[
				cn(
					'absolute left-4 z-10 cursor-text select-none text-primary-light-1 transition-all duration-200 ease-in-out font-medium leading-normal lg:font-normal',
					isFloating ? 'top-2 pb-1 text-[0.625rem] leading-none' : 'top-[25%]',
					{ 'text-primary/40 cursor-not-allowed': disabled }
				)
			]"
			@mousedown.prevent="!disabled && focus()"
		>
			{{ displayPlaceholder }}
		</label>
	</div>
</template>
